'use client';

import { usePathname } from 'next/navigation';
import { shouldRenderBanner } from '@/app/_components/shared/banner/banner.utils';
import { LabelsProvider, useLabel } from '@/app/_contexts/labels-context';
import { useLocale } from '@/app/_contexts/locale-context';
import { useAnchorScroll } from '@/app/_hooks/use-anchor-scroll';
import { useHeaderHeight } from '@/app/_hooks/use-header-height';
// Resolve the layout components (banner/header/footer/fab) from a SEPARATE,
// small generated map instead of the DI container. Going through getInjection()
// here would pull the entire eager-loaded container — and with it the full
// component-map (all 41 modules) — into the client bundle, because this is a
// Client Component. The layout map only contains these four components, yet
// still honors per-app overrides (the generator picks custom over shared), so
// an app can swap its own header/footer/etc. just like before.
import type {
  PageLayoutData,
  GlobalLabels,
} from '@/entities/models/page';
import { LAYOUT_COMPONENT_MAP } from '@/generated/layout-component-map';

/**
 * Skip link for keyboard navigation - hidden until focused
 */
function SkipLink() {
  const skipLinkText = useLabel('a11y.skipToContent', 'Skip to main content');

  return (
    <a href="#main-content" className="sr-only">
      {skipLinkText}
    </a>
  );
}

interface PageLayoutWrapperProps {
  children: React.ReactNode;
  layoutData?: PageLayoutData;
  globalLabels?: GlobalLabels;
  bannerInitialClosed?: boolean;
}

export function PageLayoutWrapper({
  children,
  layoutData,
  globalLabels,
  bannerInitialClosed,
}: PageLayoutWrapperProps) {
  const pathname = usePathname();
  const locale = useLocale();
  const headerContainerRef = useHeaderHeight();
  useAnchorScroll();

  // Resolve from the layout map (honors per-app overrides). The map is generated
  // per-app and absent from corekit's own typecheck, so it resolves to `any`
  // here; assert its known shape once (single boundary) and the reads are typed.
  type LayoutComp = React.ComponentType<Record<string, unknown>> | undefined;
  const layoutMap = LAYOUT_COMPONENT_MAP as Record<string, LayoutComp>;
  const Banner = layoutMap.banner;
  const Header = layoutMap.header;
  const Footer = layoutMap.footer;
  const Fab = layoutMap.fab;

  // Layout data is already UI-ready from the mappers (menus carry id/slug), so
  // the four component prop bundles are assembled directly — no presenter layer.
  const bannerData = layoutData?.banner;
  const fabData = layoutData?.fab;
  const headerData = {
    pages: layoutData?.navigation ?? [],
    languages: layoutData?.languages ?? [],
    actionButton: layoutData?.header?.headerCtaPrimary,
    mobileMenuSlidePosition: layoutData?.header?.mobileMenuSlidePosition,
  };
  const footerData = {
    footerMenu: layoutData?.footer?.footerMenu ?? [],
    legalMenu: layoutData?.footer?.legalMenu ?? [],
    copyright: layoutData?.footer?.copyright ?? '',
    copyrightText: layoutData?.footer?.copyrightText ?? '',
    layout: layoutData?.footer?.layout,
    theme: layoutData?.footer?.theme,
    introText: layoutData?.footer?.introText,
  };

  const logoHref = locale !== 'de' ? `/${locale}` : '/';

  const headerDisabled = layoutData?.header?.headerDisable === true;
  const footerDisabled = layoutData?.footer?.footerDisable === true;

  // Banner visibility is independent of the header: a page can hide the header
  // (navbar) via headerDisable and still show the announcement banner. The
  // banner keeps its own disable path (bannerLayout: 'disable').
  // bannerVisible also marks <main> (data-has-banner) so the CSS first-paint
  // fallback for --header-height can include the banner height — cache-safe
  // (same for all visitors); the per-visitor "closed" state is corrected
  // client-side by useHeaderHeight after the first measure.
  const bannerVisible = shouldRenderBanner(bannerData);
  // The top region carries the --header-height measurement ref, so render it
  // whenever the header OR the banner is visible.
  const showTopRegion = !headerDisabled || bannerVisible;

  return (
    <LabelsProvider labels={globalLabels ?? {}}>
      <>
        <SkipLink />
        {showTopRegion && (
          <header ref={headerContainerRef} className="fixed z-1000 w-full">
            {Banner && <Banner {...bannerData} initialClosed={bannerInitialClosed} />}
            {!headerDisabled && Header && (
              <Header
                {...headerData}
                currentPath={pathname}
                logoHref={logoHref}
                variant="default"
              />
            )}
          </header>
        )}

        <main
          id="main-content"
          className={showTopRegion ? 'pt-(--header-height)' : ''}
          data-has-banner={bannerVisible ? '' : undefined}
        >
          {children}
        </main>

        {!footerDisabled && Footer && <Footer {...footerData} />}

        {Fab && fabData && <Fab {...fabData} />}
      </>
    </LabelsProvider>
  );
}
